import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "../firebase";
import { arePenPals, sendKudos, sendPenPalRequest } from "../penpal";
import { useBlocks } from "../blocks";
import { RemovePenPalButton, RequestButton, SafetyActions } from "../components/SafetyBits";
import { TheirGarden } from "../components/Plant";
import { FlagRow } from "../components/Flags";
import { normalizeFlags } from "../flags";
import Window from "../components/Window";
import Icon from "../components/Icon";
import { Avatar, SpoonMeter } from "../components/ProfileBits";
import {
  CURRENTLY_FIELDS,
  PATTERN_CLASS,
  Pattern,
  REPLY_PACES,
  THEMES,
  ThemeTone,
} from "../profileOptions";
import "../styles.css";

const STICKER_COLORS = ["#ffe3ea", "#dff4fd", "#fff6d6", "#e4f6e4", "#f3e6ff", "#fbe9d7"];

const hasText = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

export default function PublicProfile() {
  const { id } = useParams();
  const [userData, setUserData] = useState<any>(null);
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;
      const docRef = doc(db, "users", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setUserData(docSnap.data());
      }
    };
    fetchUser();
  }, [id]);

  // Letters are only for pen pals; everyone else starts with a request.
  const [pals, setPals] = useState(false);
  const { ids: blockedIds } = useBlocks();
  useEffect(() => {
    if (user && id && user.uid !== id) arePenPals(user.uid, id).then(setPals);
  }, [user, id, blockedIds]);

  if (!userData) {
    return <p className="empty">Loading profile...</p>;
  }

  const { visibility = {} } = userData;
  const username = userData.username || "penpal";
  const isMe = user?.uid === id;
  const blocked = !!id && blockedIds.has(id);

  const handleSendMessage = () => {
    if (!user) return alert("You need to be logged in.");
    navigate(`/send?to=${id}`);
  };

  const handleSendKudos = async () => {
    if (!user || !id) return;
    const ok = await sendKudos(user.uid, id);
    if (ok !== null) alert(ok ? "Kudos sent!" : "Sorry, kudos couldn't be sent.");
  };

  const details = [
    visibility.pronouns && userData.pronouns,
    visibility.age && userData.age && `${userData.age} years old`,
    visibility.location && userData.location,
  ].filter(Boolean);

  const theme: ThemeTone = THEMES.some((t) => t.tone === userData.theme)
    ? userData.theme
    : "pink";
  const pattern: Pattern =
    userData.pattern && userData.pattern in PATTERN_CLASS ? userData.pattern : "plain";
  const pace = REPLY_PACES.find((p) => p.value === userData.replyPace)?.label;
  const spoons = typeof userData.spoons === "number" ? userData.spoons : 0;
  const stickers: string[] = Array.isArray(userData.stickers) ? userData.stickers : [];
  const access: string[] = Array.isArray(userData.access) ? userData.access : [];
  const currently = CURRENTLY_FIELDS.filter((f) => hasText(userData.currently?.[f.key]));

  const sections = [
    { title: `About @${username}`, text: userData.hobbies },
    { title: "Topics I'd love to talk about", text: userData.topics },
    { title: "Who I'd like to meet", text: userData.preferences },
    { title: "Good to know", text: userData.profileRequests },
  ].filter((s) => hasText(s.text));

  const isEmpty =
    sections.length === 0 &&
    stickers.length === 0 &&
    currently.length === 0 &&
    access.length === 0 &&
    !hasText(userData.accessNotes) &&
    !hasText(userData.boundaries);

  return (
    <Window
      title={`${username}'s_space.htm`}
      tone={theme}
      bodyClassName={PATTERN_CLASS[pattern]}
    >
      <div className="grid gap-5 md:grid-cols-[240px_minmax(0,1fr)]">
        {/* left column: the classic profile sidebar */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center text-center">
            <Avatar
              config={userData.avatarConfig}
              icon={userData.avatar}
              tone={userData.avatarTone}
              size={112}
            />
            <h1 className="window-heading mt-3 !text-2xl">
              {(visibility.name && userData.name) || `@${username}`}
            </h1>
            {visibility.name && userData.name && (
              <p className="text-sm text-ink-soft">@{username}</p>
            )}
            {details.length > 0 && (
              <p className="mt-1 text-sm">{details.join(" · ")}</p>
            )}
            {normalizeFlags(userData.flags).length > 0 && (
              <div className="mt-2">
                <FlagRow ids={normalizeFlags(userData.flags)} height={20} />
              </div>
            )}
          </div>

          {(spoons > 0 || pace) && (
            <div className="profile-box">
              <div className="profile-box-head">energy &amp; pace</div>
              <div className="profile-box-body flex flex-col items-center gap-2 text-center">
                {spoons > 0 && <SpoonMeter spoons={spoons} />}
                {pace && (
                  <p className="text-sm">
                    <Icon name="clock" size={14} /> usually writes back{" "}
                    <strong>{pace}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {!isMe && (
            <div className="profile-box">
              <div className="profile-box-head">contacting @{username}</div>
              <div className="profile-box-body grid grid-cols-1 gap-2">
                {blocked ? (
                  <p className="text-sm">You've blocked @{username}.</p>
                ) : pals ? (
                  <button onClick={handleSendMessage} className="btn btn-blue btn-sm">
                    <Icon name="mail" size={16} /> Send a letter
                  </button>
                ) : (
                  <>
                    <RequestButton
                      className="btn btn-sm"
                      send={(note) => sendPenPalRequest(user!.uid, id!, note)}
                    />
                    <p className="text-xs text-ink-soft">
                      Once they accept, you can write letters to each other.
                    </p>
                  </>
                )}
                {!blocked && (
                  <button onClick={handleSendKudos} className="btn btn-plain btn-sm">
                    <Icon name="gift" size={16} /> Send kudos
                  </button>
                )}
                {pals && !blocked && id && (
                  <RemovePenPalButton otherUid={id} username={username} onRemoved={() => setPals(false)} />
                )}
                <SafetyActions target={{ userId: id, username }} />
              </div>
            </div>
          )}
          {id && <TheirGarden uid={id} username={username} />}
        </div>

        {/* right column */}
        <div className="flex min-w-0 flex-col gap-4">
          {stickers.length > 0 && (
            <ul className="flex flex-wrap gap-2 py-1" aria-label="Interest stickers">
              {stickers.map((s, i) => (
                <li
                  key={s}
                  className="sticker"
                  style={
                    {
                      background: STICKER_COLORS[i % STICKER_COLORS.length],
                      "--tilt": `${i % 2 ? 2 : -2}deg`,
                    } as React.CSSProperties
                  }
                >
                  {s}
                </li>
              ))}
            </ul>
          )}

          {isEmpty && (
            <p className="empty">
              <span className="empty-icon">
                <Icon name="edit" size={32} tone="green" pop />
              </span>
              @{username} hasn't filled in their profile yet.
            </p>
          )}

          {sections.slice(0, 1).map((s) => (
            <Section key={s.title} title={s.title} text={s.text} />
          ))}

          {currently.length > 0 && (
            <div className="profile-box">
              <div className="profile-box-head">currently...</div>
              <dl className="profile-box-body currently-table">
                {currently.map((f) => (
                  <React.Fragment key={f.key}>
                    <dt>{f.label}</dt>
                    <dd>{userData.currently[f.key]}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}

          {sections.slice(1).map((s) => (
            <Section key={s.title} title={s.title} text={s.text} />
          ))}

          {(access.length > 0 || hasText(userData.accessNotes)) && (
            <div className="profile-box">
              <div className="profile-box-head">how I communicate</div>
              <div className="profile-box-body flex flex-col gap-2">
                {access.length > 0 && (
                  <ul className="flex flex-wrap gap-2">
                    {access.map((a) => (
                      <li key={a} className="access-chip">
                        <Icon name="check" size={14} tone="blue" /> {a}
                      </li>
                    ))}
                  </ul>
                )}
                {hasText(userData.accessNotes) && (
                  <p className="whitespace-pre-line">{userData.accessNotes}</p>
                )}
              </div>
            </div>
          )}

          {hasText(userData.boundaries) && (
            <div className="profile-box !border-tpink-deep">
              <div className="profile-box-head !bg-tpink-light">
                please don't bring up
              </div>
              <p className="profile-box-body whitespace-pre-line">
                {userData.boundaries}
              </p>
            </div>
          )}
        </div>
      </div>
    </Window>
  );
}

function Section({ title, text }: { title: string; text: string }) {
  return (
    <div className="profile-box">
      <div className="profile-box-head">{title}</div>
      <p className="profile-box-body whitespace-pre-line">{text}</p>
    </div>
  );
}
